import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import { ChevronRight, Columns3 } from 'lucide-react';
import * as React from 'react';

import { COMPACT_BAR_RESIZE } from './condense';
import { useFitHeight } from './fit-height';
import { isField } from './is-field';
import { readViewerChoice, writeViewerChoice } from './viewer-store';
import { useViewer } from './zone';

/**
 * The Team Portal's table (the two-portals design, §4.3 and §5): the kit's row, header and cell
 * primitives — so the `table` / `row` / `columnheader` / `cell` roles every spec reads stay what
 * they were — in a frame of its own, dense, with a header that stays put and rows a keyboard can
 * walk.
 *
 * **Not the kit's `Table`.** It wraps the table in a scroll container of its own, and a sticky
 * header sticks to the nearest one: inside it, the header would stick to a box that never scrolls
 * down. This frame is that container instead — sideways for a wide table on a narrow screen,
 * downwards within its height — and its width is contained (`TableFrame`'s lesson: a scroll
 * container still hands its table's width up to the page's grid).
 *
 * **One scroll, the table's** (SR-439). Unless the caller names a `maxHeight`, the frame ends where
 * the window does, with the pager and whatever else follows it in view (`useFitHeight`) — so on a
 * list screen the page does not scroll as well. Where it cannot fit — a table far down a long page,
 * or a phone whose filters take the window — the frame does not scroll down at all and the page
 * scrolls alone: one scroll either way, never the table's inside the page's. The header then stays
 * at the table's top rather than sticking, since the frame it sticks to never scrolls down — the
 * price of the one scroll, paid only where the table cannot fit.
 *
 * **Or the page's scroll** (`stickyTop`, a work page with a compact bar): the frame is no scroll
 * container at all — it clips sideways rather than scrolls, a scroll container being what a sticky
 * header sticks to — so the page scrolls and the header sticks `stickyTop` under the window's top,
 * a row walked to by the keyboard kept clear of it and of a sticky pager. A table wider than its
 * frame scrolls sideways in the frame again — a scroll container its header would stick to — so
 * there the header is moved down by hand as the page scrolls (`useHeaderFollow`), a transform only.
 *
 * **The keyboard walks the rows.** One row at a time is in the tab order; ↑ and ↓ move between
 * rows, Home and End to the first and the last, and Enter opens the row — `onOpen`, the same thing
 * a click on it does. A link or a button inside a row keeps its own Enter; the arrows work from
 * anywhere in the row but a field — and not from a menu or a listbox a row's control opened,
 * which is portalled out of the row's DOM and answers its own keys.
 *
 * **What the page has no rows for is said inside the table**, as one cell across every column, in
 * the list's own words: still loading (a skeleton shaped like rows), empty, past the end, or a
 * read that failed — which is an alert, and a row holding one is not a result.
 *
 * **A list whose next read is out keeps its rows, and says it is reading** (`busy`): a bar runs
 * along the frame's top edge, over the sticky header, a veil fades the rows back and the frame is
 * `aria-busy`, so a filter changed over a page already drawn is seen to be working — never a
 * skeleton over rows that are still the last answer. The bar and the veil wait 150 ms, so a quick
 * answer flashes neither; under reduced motion the bar stands still. With no rows drawn the
 * skeleton or the empty sentence says it, and neither is drawn. **The table's own opacity never
 * moves**: faded by a transition on the `tbody`, a list in Safari drew the answer's rows and then
 * left them blank, each coming back only as the pointer passed over it — a transition's end that
 * Safari does not paint back into the scroll container.
 *
 * **A column picker where a list has optional columns**, remembered per viewer on this browser.
 * The default is the columns the screen showed before there was a picker; the optional ones it
 * adds are hidden until asked for, and always come after the rest, so a column's place does not
 * depend on what else is shown.
 *
 * **A selection column where a list has bulk actions** (`selectable`, off by default): a box per
 * row named after it ("Select Dana Whitfield") and one in the header for every row shown. It is a
 * column of controls and nothing else — the row keys, the focus and Enter are what they were, and a
 * press on a box is the box's, never the row's open. The list owns what is selected, as it owns the
 * open row; a table that kept it would lose it to the next page of rows.
 *
 * **Groups where a list groups its rows** (`grouping`, off by default): one level or several,
 * outermost first, each a heading row across the table — "State: NY (4)" — with a fold and, in a
 * selectable table, a box for every row under it. A heading is not a record: it carries no
 * `data-row`, takes no part in ↑/↓ and opens nothing, so the keys walk the records drawn as they
 * did, a folded group's skipped. Inside a group the rows keep the order they came in — the list's
 * sort — and the groups follow the level's own order. The list keeps what is folded, as it keeps
 * what is selected. A list whose page is one of many says the whole result's count in a heading
 * (`count`), and one grouping by stored values says each by its label (`display`).
 */

/** A list's selection, which the list keeps: the keys ticked, and what a tick or the header's box
 *  asks for. */
export interface IDataSelection<Row> {
  selected: ReadonlySet<string>;
  onSelect: (keys: string[]) => void;
  /** What a row's box is named after — "Select <name>". */
  name: (row: Row) => string;
}

/** One level of a list's grouping. */
export interface IDataGroupLevel<Row> {
  id: string;
  /** What a group's heading names the level — "State". */
  label: string;
  /** The value a row is grouped under, as the heading says it. */
  value: (row: Row) => string;
  /** The order of the level's groups. */
  compare: (left: string, right: string) => number;
  /** How the heading says `value`, where `value` is a key rather than words — a list grouping by
   *  a stored value draws its label. */
  display?: (value: string) => string;
  /** What the heading says in place of "label: value" — a group that is a part of the list, such as
   *  Reinstatement's exceptions pinned on top, rather than a value of its rows. */
  name?: (value: string) => string;
}

/** A list's grouping, which the list keeps: its levels, outermost first, and the groups folded. */
export interface IDataGrouping<Row> {
  levels: readonly IDataGroupLevel<Row>[];
  /** The groups folded shut, by the key each heading carries as `data-group`. */
  collapsed: ReadonlySet<string>;
  onToggle: (key: string) => void;
  /** What a heading says after its name in place of the rows it holds on this page — a list whose
   *  page is one of many counts the whole result's group (`path` is each level's `value`,
   *  outermost first). */
  count?: (path: readonly string[], rows: readonly Row[]) => React.ReactNode;
}

export interface IDataColumn<Row> {
  /** Stable, and the key a viewer's choice is kept under. */
  id: string;
  header: React.ReactNode;
  /** What the picker calls the column, where `header` is not plain text. */
  label?: string;
  cell: (row: Row) => React.ReactNode;
  /** Right-aligned, in tabular figures. */
  numeric?: boolean;
  /** Offered in the column picker. */
  optional?: boolean;
  /** An optional column hidden until a viewer asks for it. */
  hidden?: boolean;
  className?: string;
  headClassName?: string;
}

/** What a page with no row to show says instead, in one cell across the table. */
export type DataTableState =
  | { kind: 'loading' }
  | { kind: 'empty'; content: React.ReactNode }
  | { kind: 'error'; content: React.ReactNode };

export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  onOpen,
  selected,
  state,
  pickerId,
  pickerNote,
  label,
  minWidth,
  maxHeight,
  stickyTop,
  toolbar,
  rowProps,
  selectable,
  grouping,
  busy = false,
  className
}: {
  columns: IDataColumn<Row>[];
  /** Null while there is nothing to show — `state` says why. */
  rows: Nullable<Row[]>;
  rowKey: (row: Row) => string;
  /** What Enter and a click on the row do. A row with no record of its own passes nothing. */
  onOpen?: (row: Row) => void;
  /** The row whose record is open beside the list — `aria-current`. */
  selected?: Nullable<string>;
  /** Said when `rows` is null, or empty. */
  state?: DataTableState;
  /** Where a viewer's column choice is kept; no picker without it. */
  pickerId?: string;
  /** Said in the column picker under its own line — what the list cannot offer, and why. */
  pickerNote?: React.ReactNode;
  /** The table's accessible name, where the page names more than one. */
  label?: string;
  /** A width below which the frame scrolls sideways rather than crush a column. */
  minWidth?: string;
  /** How tall the frame may grow before it scrolls — `none` for a table inside a longer page.
   *  Unnamed, the frame fits the window (above). */
  maxHeight?: string;
  /** The page scrolls, not the frame, and the header sticks this far under the window's top. */
  stickyTop?: string;
  /** Drawn at the right of the picker's line — a count, a note. */
  toolbar?: React.ReactNode;
  rowProps?: (row: Row) => React.HTMLAttributes<HTMLTableRowElement>;
  /** A box per row, for a list's bulk actions. */
  selectable?: IDataSelection<Row>;
  /** Headings over the rows, by one or more of their values. */
  grouping?: IDataGrouping<Row>;
  /** The list's next read is out: the rows drawn are the last answer, kept while it comes. */
  busy?: boolean;
  className?: string;
}) {
  const [visible, setVisible] = useColumnChoice<Row>(pickerId);
  const keys = rows?.map(rowKey) ?? [];
  const shown = [
    ...(selectable ? [selectionColumn(selectable, keys, rowKey)] : []),
    ...columns.filter((column) => visible(column))
  ];
  const lines = layOut(rows ?? [], grouping);
  // The rows drawn, in their order — what ↑/↓, Home, End and Enter walk.
  const drawn = lines.flatMap((line) => (line.kind === 'row' ? [line.row] : []));
  const [focus, setFocus] = React.useState(0);
  const current = Math.min(focus, Math.max(0, drawn.length - 1));

  const onKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>, index: number) => {
    // React bubbles a key out of a portal through the component that renders it, so an open
    // Select in a row would walk the rows too: a key some control already answered, or one
    // pressed outside the row's own DOM, is not the row's.
    if (event.defaultPrevented || !event.currentTarget.contains(event.target as Node)) return;
    if (isField(event.target)) return;
    const onRow = event.target === event.currentTarget;
    // The row's siblings, read off the page: the kit's body takes no ref.
    const siblings =
      event.currentTarget.parentElement?.querySelectorAll<HTMLTableRowElement>(
        ':scope > tr[data-row]'
      ) ?? [];
    const moveTo = (target: number) => {
      const clamped = Math.max(0, Math.min(siblings.length - 1, target));
      setFocus(clamped);
      siblings[clamped]?.focus();
    };
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveTo(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveTo(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        moveTo(0);
        break;
      case 'End':
        event.preventDefault();
        moveTo(drawn.length - 1);
        break;
      case 'Enter':
        // A link or a button in the row answers its own Enter.
        if (onRow && onOpen) {
          event.preventDefault();
          onOpen(drawn[index]);
        }
        break;
    }
  };

  const frame = React.useRef<HTMLDivElement>(null);
  const fitted = useFitHeight(frame, maxHeight === undefined && stickyTop === undefined);
  const frameHeight = stickyTop !== undefined ? 'none' : (maxHeight ?? (fitted === null ? UNFITTED : `${fitted}px`));
  const wide = useWide(frame, stickyTop !== undefined);
  // A row opened beside the table narrows it into a scrolling frame, and the row's Details button,
  // last and focused, scrolls the frame to itself — the rows' names out of sight (SR-498). A row
  // opened from none, and the frame turned scrolling, start at its first column; walking from one
  // opened row to the next keeps the scroll the focus asks for.
  const opened = selected !== undefined && selected !== null;
  React.useLayoutEffect(() => {
    if (opened && frame.current) frame.current.scrollLeft = 0;
  }, [opened, wide]);
  const pageSticky = stickyTop !== undefined && !wide;
  const probe = React.useRef<HTMLSpanElement>(null);
  useHeaderFollow(frame, probe, stickyTop !== undefined && wide);

  const nothing = rows === null || rows.length === 0;
  const optional = columns.filter((column) => column.optional);

  return (
    <div className={cn('grid gap-1.5', className)}>
      {(pickerId && optional.length > 0) || toolbar ? (
        <div className="flex min-h-7 flex-wrap items-center justify-end gap-2">
          {toolbar}
          {pickerId && optional.length > 0 && (
            <ColumnPicker
              columns={optional}
              visible={visible}
              onChange={setVisible}
              note={pickerNote}
            />
          )}
        </div>
      ) : null}
      <div className="relative">
        {stickyTop !== undefined && (
          // Where `stickyTop` falls, in pixels, for the header moved by hand.
          <span ref={probe} aria-hidden className="invisible absolute" style={{ top: stickyTop }} />
        )}
        <div
          ref={frame}
          data-slot="data-table"
          aria-busy={busy || undefined}
          // `relative`: the containing block of what a cell positions absolutely — a link's
          // `sr-only` words among them — so the frame clips it; held by a box outside, it hung
          // below the rows and lengthened the page (SR-439).
          className={cn(
            'relative rounded-md border border-border bg-card [contain:inline-size]',
            pageSticky ? 'overflow-x-clip' : 'overflow-auto'
          )}
          style={{ maxHeight: frameHeight }}
        >
          <table
            aria-label={label}
            className="w-full caption-bottom border-separate border-spacing-0 text-[0.8125rem]"
            style={minWidth ? { minWidth } : undefined}
          >
            <TableHeader className="[&_tr]:border-0">
              <TableRow className="hover:bg-transparent">
                {shown.map((column) => (
                  <TableHead
                    key={column.id}
                    style={pageSticky ? { top: stickyTop } : undefined}
                    className={cn(
                      // `z-[2]`: above a cell's control raised over its neighbours (Lists' editor).
                      'sticky top-0 z-[2] h-8 border-b border-border bg-[var(--color-off-white)] px-3 text-xs font-medium whitespace-nowrap text-[var(--veritant-royal-70)]',
                      column.numeric && 'text-right',
                      column.headClassName
                    )}
                  >
                    {column.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody className="[&_tr:last-child>td]:border-0">
              {!nothing &&
                lines.map((line) => {
                  if (line.kind === 'group') {
                    return (
                      <GroupHeading
                        key={`group:${line.key}`}
                        line={line}
                        span={shown.length}
                        collapsed={grouping?.collapsed.has(line.key) ?? false}
                        onToggle={() => grouping?.onToggle(line.key)}
                        count={grouping?.count}
                        selectable={selectable}
                        rowKey={rowKey}
                      />
                    );
                  }
                  const { row, index } = line;
                  const key = rowKey(row);
                  const extra = rowProps?.(row) ?? {};
                  return (
                    <TableRow
                      key={key}
                      {...extra}
                      data-row=""
                      tabIndex={index === current ? 0 : -1}
                      aria-current={selected === key ? 'true' : undefined}
                      onFocus={(event) => {
                        if (event.target === event.currentTarget) setFocus(index);
                      }}
                      onKeyDown={(event) => onKeyDown(event, index)}
                      style={
                        stickyTop !== undefined
                          ? { scrollMarginTop: `calc(${stickyTop} + 2.5rem)`, scrollMarginBottom: '3.5rem', ...extra.style }
                          : extra.style
                      }
                      onClick={
                        onOpen
                          ? (event) => {
                              // A press on a link or a control in the row is that control's, and one
                              // outside the row's own DOM — a dialog a row's press opened, which React
                              // bubbles out of its portal through the row — is not the row's at all.
                              if (!event.currentTarget.contains(event.target as Node)) return;
                              if (isInteractive(event.target, event.currentTarget)) return;
                              onOpen(row);
                            }
                          : undefined
                      }
                      className={cn(
                        'border-0 outline-none focus-visible:bg-[var(--accent)] focus-visible:shadow-[inset_3px_0_0_var(--color-teal)]',
                        onOpen && 'cursor-pointer',
                        selected === key &&
                          'bg-[color-mix(in_srgb,var(--color-teal)_8%,white)] shadow-[inset_3px_0_0_var(--color-teal)] hover:bg-[color-mix(in_srgb,var(--color-teal)_11%,white)]',
                        extra.className
                      )}
                    >
                      {shown.map((column) => (
                        <TableCell
                          key={column.id}
                          className={cn(
                            'h-8 border-b border-border px-3 py-1.5 align-top',
                            column.numeric && 'text-right tabular-nums',
                            column.className
                          )}
                        >
                          {column.cell(row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              {nothing && state && (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={shown.length}
                    className={cn(
                      'px-3 text-center whitespace-normal text-muted-foreground',
                      state.kind === 'loading' ? 'py-2' : 'py-8'
                    )}
                  >
                    {state.kind === 'loading' ? (
                      <span className="grid gap-2">
                        {Array.from({ length: 6 }, (_, index) => (
                          <Skeleton key={index} className="h-6 w-full" />
                        ))}
                      </span>
                    ) : state.kind === 'error' ? (
                      <div className="text-left">{state.content}</div>
                    ) : (
                      state.content
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </table>
        </div>
        {busy && !nothing && (
          <>
            <ReadingVeil />
            <ReadingBar />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * A sticky header where the frame scrolls sideways, and so cannot stick to the page: the `thead`
 * moved down by the part of the frame scrolled above `probe`'s top, as far as the frame's end —
 * written in the page's scroll event, a transform, nothing laid out again — and placed again on
 * each render and as the page's height changes, which move the frame without a scroll.
 */
function useHeaderFollow(
  frame: React.RefObject<HTMLDivElement>,
  probe: React.RefObject<HTMLSpanElement>,
  enabled: boolean
): void {
  const placeRef = React.useRef<Nullable<() => void>>(null);
  React.useLayoutEffect(() => placeRef.current?.());
  React.useLayoutEffect(() => {
    const box = frame.current;
    const head = box?.querySelector('thead');
    if (!enabled || !box || !head) return;
    const place = () => {
      const top = probe.current?.offsetTop ?? 0;
      const rect = box.getBoundingClientRect();
      // Floored: a fraction of a pixel under what sticks above, never a gap the rows show through.
      const offset = Math.max(0, Math.min(Math.floor(top - rect.top - box.clientTop), box.clientHeight - head.offsetHeight));
      head.style.transform = offset > 0 ? `translateY(${offset}px)` : '';
    };
    head.style.position = 'relative';
    head.style.zIndex = '2';
    place();
    placeRef.current = place;
    const observer = new ResizeObserver(place);
    observer.observe(document.documentElement);
    window.addEventListener('scroll', place, { passive: true });
    window.addEventListener('resize', place);
    window.addEventListener(COMPACT_BAR_RESIZE, place);
    return () => {
      placeRef.current = null;
      observer.disconnect();
      window.removeEventListener('scroll', place);
      window.removeEventListener('resize', place);
      window.removeEventListener(COMPACT_BAR_RESIZE, place);
      head.style.transform = '';
      head.style.position = '';
      head.style.zIndex = '';
    };
  }, [frame, probe, enabled]);
}

/** Whether the table is wider than its frame — watched only where the page scrolls (`stickyTop`). */
function useWide(frame: React.RefObject<HTMLDivElement>, enabled: boolean): boolean {
  const [wide, setWide] = React.useState(false);
  React.useLayoutEffect(() => {
    const box = frame.current;
    const table = box?.firstElementChild;
    if (!enabled || !box || !(table instanceof HTMLElement)) {
      setWide(false);
      return;
    }
    const measure = () => setWide(table.offsetWidth > box.clientWidth + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    observer.observe(table);
    return () => observer.disconnect();
  }, [frame, enabled]);
  return wide;
}

/** The frame's height where it cannot fit the window: all its rows, the page's scroll the only one.
 *  `calc(100dvh - 13rem)` before SR-439, a guess at what sits above, which scrolled twice. */
const UNFITTED = 'none';

/** The rows faded back while the next read is out: the card's colour at half strength over the
 *  frame, which is what half the rows' opacity drew over the card. `z-[1]`: over the rows and a
 *  cell's control raised over its neighbours (Lists' editor), under the sticky header (`z-[2]`),
 *  which stays as it was. A box mounted and unmounted whole, so a read that lands takes the fade
 *  off at once and nothing in the table changes layer — see the component's note on Safari. */
function ReadingVeil() {
  return (
    <div
      aria-hidden
      data-list-reading-veil
      className="pointer-events-none absolute inset-px z-[1] rounded-md bg-card/50 animate-[veritant-fade-in_0.2s_ease-out_0.15s_both]"
    />
  );
}

/** A bar running along the frame's top edge while the next read is out — `z-[3]`, over the
 *  sticky header (`z-[2]`), which would otherwise cover it. Decoration: the frame says
 *  `aria-busy`. Outside the scroll container, so it stays put while the rows scroll; the top
 *  search's bar (`command-palette.tsx`) is its twin. */
function ReadingBar() {
  return (
    <div
      aria-hidden
      data-list-reading-bar
      className="pointer-events-none absolute inset-x-px top-px z-[3] h-0.5 overflow-hidden rounded-t-md animate-[veritant-fade-in_0.2s_ease-out_0.15s_both]"
    >
      <span className="absolute inset-y-0 left-0 w-1/3 animate-[veritant-splash_1.1s_ease-in-out_infinite] rounded-full bg-[var(--color-sapphire)] motion-reduce:w-full motion-reduce:animate-none motion-reduce:opacity-40" />
    </div>
  );
}

/** A heading over a group's rows, or one of its rows — the table's body, in the order drawn. */
type Line<Row> =
  | {
      kind: 'group';
      key: string;
      level: IDataGroupLevel<Row>;
      value: string;
      path: readonly string[];
      depth: number;
      rows: Row[];
    }
  | { kind: 'row'; row: Row; index: number };

/** The key a group is folded under: every level's id and value down to it, so "Firm: LPL" under
 *  "State: NY" and under "State: TX" are two groups, folded apart. */
function groupKey(path: readonly (readonly [string, string])[]): string {
  return JSON.stringify(path);
}

/** The body's lines: without a grouping, the rows; with one, each group's heading before what it
 *  holds — its groups or its rows — and nothing under a heading that is folded. */
function layOut<Row>(rows: readonly Row[], grouping: IDataGrouping<Row> | undefined): Line<Row>[] {
  const lines: Line<Row>[] = [];
  let index = 0;
  const walk = (members: readonly Row[], depth: number, path: (readonly [string, string])[]) => {
    const level = grouping?.levels[depth];
    if (!grouping || !level) {
      for (const row of members) lines.push({ kind: 'row', row, index: index++ });
      return;
    }
    const groups = new Map<string, Row[]>();
    for (const row of members) {
      const value = level.value(row);
      const group = groups.get(value);
      if (group) group.push(row);
      else groups.set(value, [row]);
    }
    const ordered = [...groups].sort(([left], [right]) => level.compare(left, right));
    for (const [value, under] of ordered) {
      const at = [...path, [level.id, value] as const];
      const key = groupKey(at);
      lines.push({ kind: 'group', key, level, value, path: at.map(([, part]) => part), depth, rows: under });
      if (!grouping.collapsed.has(key)) walk(under, depth + 1, at);
    }
  };
  walk(rows, 0, []);
  return lines;
}

/** A group's heading: its fold, its box in a selectable table, and "State: NY (4)". */
function GroupHeading<Row>({
  line,
  span,
  collapsed,
  onToggle,
  count,
  selectable,
  rowKey
}: {
  line: Extract<Line<Row>, { kind: 'group' }>;
  span: number;
  collapsed: boolean;
  onToggle: () => void;
  count?: IDataGrouping<Row>['count'];
  selectable?: IDataSelection<Row>;
  rowKey: (row: Row) => string;
}) {
  const name =
    line.level.name?.(line.value) ?? `${line.level.label}: ${line.level.display?.(line.value) ?? line.value}`;
  const keys = line.rows.map(rowKey);
  const ticked = selectable ? keys.filter((key) => selectable.selected.has(key)) : [];
  const all = ticked.length === keys.length;
  return (
    <TableRow data-group={line.key} data-depth={line.depth} className="hover:bg-transparent">
      <TableCell
        colSpan={span}
        className="h-8 border-b border-border bg-[color-mix(in_srgb,var(--color-off-white)_60%,white)] px-3 py-1"
      >
        <span className="flex items-center gap-2" style={{ paddingLeft: `${line.depth * 1.5}rem` }}>
          <button
            type="button"
            aria-expanded={!collapsed}
            aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${name}`}
            onClick={onToggle}
            className="rounded p-0.5 text-[var(--veritant-royal-70)] outline-none hover:bg-white focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronRight className={cn('size-4 transition-transform', !collapsed && 'rotate-90')} aria-hidden />
          </button>
          {selectable && (
            <Checkbox
              aria-label={`Select every row in ${name}`}
              checked={all ? true : ticked.length > 0 ? 'indeterminate' : false}
              onCheckedChange={() => {
                const others = [...selectable.selected].filter((key) => !keys.includes(key));
                selectable.onSelect(all ? others : [...others, ...keys]);
              }}
            />
          )}
          <span data-group-label="" className="rounded-full border border-border bg-white px-2.5 py-0.5 font-medium">
            {name}{' '}
            <span className="font-normal tabular-nums text-muted-foreground">
              {count ? count(line.path, line.rows) : `(${keys.length})`}
            </span>
          </span>
        </span>
      </TableCell>
    </TableRow>
  );
}

/** The selection column: a box per row, and the header's for every row shown — checked when all
 *  are, mixed when some are, and a press on it ticks all or clears them. */
function selectionColumn<Row>(
  selection: IDataSelection<Row>,
  keys: readonly string[],
  rowKey: (row: Row) => string
): IDataColumn<Row> {
  const ticked = keys.filter((key) => selection.selected.has(key));
  const all = keys.length > 0 && ticked.length === keys.length;
  const others = [...selection.selected].filter((key) => !keys.includes(key));
  return {
    id: '__select',
    header: (
      <Checkbox
        aria-label="Select every row"
        checked={all ? true : ticked.length > 0 ? 'indeterminate' : false}
        disabled={keys.length === 0}
        onCheckedChange={() => selection.onSelect(all ? others : [...others, ...keys])}
      />
    ),
    label: 'Select',
    className: 'w-9 pr-0',
    headClassName: 'w-9 pr-0',
    cell: (row) => {
      const key = rowKey(row);
      const on = selection.selected.has(key);
      return (
        <Checkbox
          aria-label={`Select ${selection.name(row)}`}
          checked={on}
          onCheckedChange={(next) =>
            selection.onSelect(
              next === true ? [...selection.selected, key] : [...selection.selected].filter((entry) => entry !== key)
            )
          }
        />
      );
    }
  };
}

/** The optional columns, as boxes. */
function ColumnPicker<Row>({
  columns,
  visible,
  onChange,
  note
}: {
  columns: IDataColumn<Row>[];
  visible: (column: IDataColumn<Row>) => boolean;
  onChange: (id: string, on: boolean) => void;
  note?: React.ReactNode;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="h-7 gap-1.5 font-normal">
          <Columns3 className="size-4" aria-hidden />
          Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        aria-label="Columns"
        className="grid max-h-[min(28rem,70dvh)] w-64 gap-2 overflow-y-auto p-3 text-[0.8125rem]"
      >
        <p className="text-xs text-muted-foreground">Kept for you on this browser.</p>
        {note && <p className="text-xs text-muted-foreground">{note}</p>}
        {columns.map((column) => (
          <label key={column.id} className="flex items-center gap-2">
            <Checkbox
              checked={visible(column)}
              onCheckedChange={(next) => onChange(column.id, next === true)}
            />
            {column.label ?? (typeof column.header === 'string' ? column.header : column.id)}
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Which columns a viewer shows — the optional ones they switched, over each column's default. Kept
 * in local storage per list and viewer, through `viewer-store.ts` — a store that refuses, or holds
 * something unreadable, leaves the defaults, and the page works the same.
 */
function useColumnChoice<Row>(
  pickerId: string | undefined
): [(column: IDataColumn<Row>) => boolean, (id: string, on: boolean) => void] {
  const viewer = useViewer();
  const key = pickerId ? `veritant.columns.${pickerId}.${viewer}` : null;
  const [choice, setChoice] = React.useState<Record<string, boolean>>(() => readChoice(key));
  React.useEffect(() => setChoice(readChoice(key)), [key]);

  const visible = (column: IDataColumn<Row>) =>
    !column.optional || (choice[column.id] ?? !column.hidden);

  const set = (id: string, on: boolean) => {
    setChoice((current) => {
      const next = { ...current, [id]: on };
      writeViewerChoice(key, next);
      return next;
    });
  };
  return [visible, set];
}

function readChoice(key: Nullable<string>): Record<string, boolean> {
  return Object.fromEntries(
    Object.entries(readViewerChoice(key)).filter(
      (entry): entry is [string, boolean] => typeof entry[1] === 'boolean'
    )
  );
}

/** Whether a press landed on something of its own inside the row — a link, a button, a field, a
 *  fold's summary (a row's "and N more reasons", SR-414). */
function isInteractive(target: EventTarget, row: HTMLElement): boolean {
  if (!(target instanceof Element)) return false;
  const control = target.closest(
    'a, button, input, select, textarea, label, summary, [role="button"]'
  );
  return control !== null && row.contains(control) && control !== row;
}
